import {
  Alert,
  Box,
  Button,
  Container,
  FormField,
  Header,
  KeyValuePairs,
  PieChart,
  Select,
  SpaceBetween,
  Spinner,
  StatusIndicator,
} from '@amzn/awsui-components-react';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { get, sum, values } from 'lodash';
import { getStudySummaryActions } from '../../study_summary/redux';
import STUDY_SUMMARY_TYPES from '../../study_summary/constants/studySummaries';
import { computeNextSweepStep } from '../utils/surveySettle';

const TYPE_LABELS = {
  LaborProjectSubstantiation: 'Substantiation provider surveys',
  LaborBCSubstantiation: 'Substantiation primary owner surveys',
  Labor: 'Single allocation surveys',
  LaborDual: 'Dual allocation surveys',
};

// A compliance season spans the whole credit year, so the card always shows
// all survey types (like the prototype) rather than the studyType-scoped
// subset used by the per-study dashboard views.
const SEASON_SURVEY_TYPES = [
  'LaborProjectSubstantiation',
  'LaborBCSubstantiation',
  'Labor',
  'LaborDual',
];

const STATUS_COLORS = {
  Submitted: '#037f0c',
  InProgress: '#0073bb',
  Created: '#687078',
};

const bigNumber = value => (
  <Box fontSize="display-l" fontWeight="bold">
    {String(value)}
  </Box>
);

/**
 * Surveys overview card. All data is REAL (getStudySummary aggByStatus): the
 * per-survey-type counts and the status donut. There is no per-business-unit or
 * per-date survey aggregation API, so no such chart is shown.
 */
const SurveysCard = ({ onViewAll, summary, isFetching, error, fetchSummary, clearSurveys }) => {
  const surveyTypes = SEASON_SURVEY_TYPES;
  const [chartType, setChartType] = useState({ label: 'All surveys', value: 'all' });

  // Gate the donut on every request SETTLING (success, failure, or empty), not
  // on every type having data. `isFetching` here is study_summary's per-type
  // fetching flag for SURVEYS (fetchingByType.SURVEYS), so the concurrent
  // SEASON/QRE requests from sibling cards no longer produce false settle edges
  // like the old shared boolean did. A type with no surveys or a failed request
  // is never added to surveyTypes, so the old has()-based gate spun forever.
  // Fire one request at a time and count each true->false edge — one clean
  // settle signal per request. After all types settle the card renders
  // regardless of whether each type returned data.
  const [settledCount, setSettledCount] = useState(0);
  // Survey types whose request failed during the current sweep. Surfaced as a
  // retry affordance instead of silently rendering the donut as "complete".
  const [failedTypes, setFailedTypes] = useState([]);
  const wasFetching = useRef(false);
  // Settle edges count only once our sweep has started; a start requested while another
  // SURVEYS request is in flight waits for it to settle.
  const sweepActive = useRef(false);
  const pendingStart = useRef(false);

  // Start (or restart) the serial sweep from the first survey type. Clears the
  // SURVEYS slice first so a re-mount doesn't accumulate onto stale aggByStatus
  // totals (the reducer sums status counts across the sweep), and resets the
  // local settle/failure trackers.
  const runSweep = useCallback(() => {
    if (isFetching) {
      pendingStart.current = true;
      return;
    }
    pendingStart.current = false;
    sweepActive.current = true;
    clearSurveys();
    setSettledCount(0);
    setFailedTypes([]);
    wasFetching.current = false;
    fetchSummary({
      body: { summaryType: STUDY_SUMMARY_TYPES.SURVEYS.key, surveyType: surveyTypes[0] },
    });
  }, [clearSurveys, fetchSummary, surveyTypes, isFetching]);

  // Kick off the first sweep on mount.
  useEffect(() => {
    runSweep();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // When the in-flight request settles, record any failure, count it, and fire
  // the next request. The dispatch is kept as a plain effect body statement (not
  // inside a state updater) because a React state updater must be a pure
  // function — dispatching a side-effect inside one is incorrect and can fire
  // duplicate requests if React invokes the updater more than once.
  useEffect(() => {
    if (!sweepActive.current) {
      wasFetching.current = isFetching;
      return;
    }
    if (wasFetching.current && !isFetching) {
      // The request that just settled is for surveyTypes[settledCount]. The
      // reducer clears errorByType.SURVEYS on each new request BEGIN, so a
      // truthy `error` at this settle edge means this request is the one that
      // failed.
      if (error) {
        const failedType = surveyTypes[settledCount];
        setFailedTypes(prev => (prev.includes(failedType) ? prev : [...prev, failedType]));
      }
      const { next, shouldFetchNext, nextIndex } = computeNextSweepStep(
        settledCount,
        surveyTypes.length,
      );
      setSettledCount(next);
      if (shouldFetchNext) {
        fetchSummary({
          body: {
            summaryType: STUDY_SUMMARY_TYPES.SURVEYS.key,
            surveyType: surveyTypes[nextIndex],
          },
        });
      }
    }
    wasFetching.current = isFetching;
  }, [isFetching, surveyTypes, fetchSummary, settledCount, error]);

  useEffect(() => {
    if (pendingStart.current && !isFetching) runSweep();
  }, [isFetching, runSweep]);

  const typeData = get(summary, 'surveyTypes', {});
  const mergedAgg = get(summary, 'aggByStatus', {});
  const selectedAgg =
    chartType.value === 'all' ? mergedAgg : get(typeData, [chartType.value, 'aggByStatus'], {});
  const donutTotal = sum(values(selectedAgg)) || 0;
  const allTypesReceived = settledCount >= surveyTypes.length;

  const typeTotal = type => {
    const agg = get(typeData, [type, 'aggByStatus'], null);
    return agg ? sum(values(agg)) : '—';
  };

  const typeValue = type =>
    failedTypes.includes(type) ? (
      <StatusIndicator type="error">Failed</StatusIndicator>
    ) : (
      bigNumber(typeTotal(type))
    );

  const pieData = Object.keys(STATUS_COLORS)
    .filter(k => get(selectedAgg, k, 0) > 0)
    .map(k => ({ title: k, value: selectedAgg[k], color: STATUS_COLORS[k] }));

  const typeOptions = [
    { label: 'All surveys', value: 'all' },
    ...surveyTypes.map(t => ({ label: TYPE_LABELS[t] || t, value: t })),
  ];

  return (
    <Container
      header={
        <Header variant="h2" actions={<Button onClick={onViewAll}>View all surveys</Button>}>
          Surveys
        </Header>
      }
    >
      <SpaceBetween size="l">
        {failedTypes.length > 0 && (
          <Alert
            type="warning"
            header="Some survey types failed to load"
            action={
              <Button onClick={runSweep} disabled={isFetching}>
                Retry
              </Button>
            }
          >
            {`Could not load: ${failedTypes.map(t => TYPE_LABELS[t] || t).join(', ')}.`}
          </Alert>
        )}
        <KeyValuePairs
          columns={Math.max(surveyTypes.length, 1)}
          items={surveyTypes.map(type => ({
            label: TYPE_LABELS[type] || type,
            value: typeValue(type),
          }))}
        />
        {!allTypesReceived ? (
          <Box textAlign="center" padding="l">
            <Spinner size="normal" /> Loading survey metrics…
          </Box>
        ) : (
          <SpaceBetween size="m">
            <div style={{ maxWidth: 296 }}>
              <FormField label="Survey type">
                <Select
                  selectedOption={chartType}
                  onChange={({ detail }) => setChartType(detail.selectedOption)}
                  options={typeOptions}
                />
              </FormField>
            </div>
            {pieData.length > 0 ? (
              <PieChart
                data={pieData}
                size="medium"
                variant="donut"
                hideFilter
                innerMetricValue={String(donutTotal)}
                innerMetricDescription="Total"
                segmentDescription={(segment, total) =>
                  `${segment.value} surveys (${((segment.value / total) * 100).toFixed(0)}%)`
                }
                ariaLabel="Survey status distribution"
              />
            ) : (
              <Box color="text-body-secondary" textAlign="center" padding="l">
                No survey data for this selection.
              </Box>
            )}
          </SpaceBetween>
        )}
      </SpaceBetween>
    </Container>
  );
};

SurveysCard.propTypes = {
  onViewAll: PropTypes.func.isRequired,
  summary: PropTypes.object,
  isFetching: PropTypes.bool,
  error: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
  fetchSummary: PropTypes.func.isRequired,
  clearSurveys: PropTypes.func.isRequired,
};

SurveysCard.defaultProps = {
  summary: {},
  isFetching: false,
  error: null,
};

const mapStateToProps = state => ({
  summary: get(state, `entities.studySummary.data.${STUDY_SUMMARY_TYPES.SURVEYS.key}`, {}) || {},
  isFetching: get(
    state,
    ['entities', 'studySummary', 'fetchingByType', STUDY_SUMMARY_TYPES.SURVEYS.key],
    false,
  ),
  error: get(
    state,
    ['entities', 'studySummary', 'errorByType', STUDY_SUMMARY_TYPES.SURVEYS.key],
    null,
  ),
});

const mapDispatchToProps = dispatch => ({
  fetchSummary: payload => dispatch(getStudySummaryActions.BEGIN(payload)),
  clearSurveys: () => dispatch(getStudySummaryActions.CLEAR(STUDY_SUMMARY_TYPES.SURVEYS.key)),
});

export default connect(mapStateToProps, mapDispatchToProps)(SurveysCard);
